<script setup lang="ts">
import { getAuthorDisplayName } from '@@/bits/author'
import { formatDate } from '@@/bits/date'
import device from '@@/bits/device'
import { __ } from '@@/bits/intl'
import { postColorRingWithOffsetTailwindClass } from '@@/bits/post_color'
import { getPostPublishedAt } from '@@/bits/post_timestamps'
import { isRegistered as isUserRegistered } from '@@/bits/user_model'
import OzRelativeTime from '@@/library/v4/components/OzRelativeTime.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfaceSimulatedAttributionStore } from '@@/pinia/surface_simulated_attribution_store'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import type { HashId, Id, Post } from '@@/types'
import AvatarImage from '@@/vuecomponents/avatar_image.vue'
import SurfaceTranslationIndicator, {
  SurfaceTranslationIndicatorUsageContext,
} from '@@/vuecomponents/SurfaceTranslationIndicator.vue'
import { useAuthor } from '@@/vuecomposables/useAuthor'
import emitClickOnTouch from '@@/vuedirectives/emit_click_on_touch'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

const vEmitClickOnTouch = emitClickOnTouch

const props = withDefaults(
  defineProps<{
    post: Post
    avatarSize: 16 | 20 | 24
    darkMode?: boolean | 'auto'
    xPublishedAt?: boolean
    usageContext?: 'postExpandedView' | 'postView'
    isTimelinePreview?: boolean
  }>(),
  {
    xPublishedAt: true,
    darkMode: 'auto',
    usageContext: 'postView',
    isTimelinePreview: false,
  },
)

const surfaceStore = useSurfaceStore()
const { isScreenshotMode } = storeToRefs(surfaceStore)
const surfaceGuestStore = useSurfaceGuestStore()
const surfaceSimulatedAttributionStore = useSurfaceSimulatedAttributionStore()
const surfaceTranslationsStore = useSurfaceTranslationsStore()
const { wishIdsBeingTranslated } = storeToRefs(surfaceTranslationsStore)

const shouldShowTranslatedWishIcon = computed<boolean>(() => {
  return surfaceTranslationsStore.isWishTranslatedOrBeingTranslated(props.post.id)
})

const isWishBeingTranslated = computed<boolean>(() => {
  if (!props.post.id) return false
  return wishIdsBeingTranslated.value.has(props.post.id)
})

const authorId = computed<Id | HashId | undefined>(() => props.post.author_hashid || props.post.author_id)

const { authorRole, xAuthorRole, author, username } = useAuthor({
  authorId: authorId.value,
  postOrCommentHashId: props.post.hashid,
})

const authorName = computed<string>(() => {
  // If a simulated attribution exists in the onboarding demo, we return the name of the simulated user
  if (props.post.hashid && surfaceSimulatedAttributionStore.attributions[props.post.hashid]) {
    return surfaceSimulatedAttributionStore.attributions[props.post.hashid].name
  }

  const name =
    surfaceGuestStore.shouldEnableAnonymousAttribution &&
    !isRegistered.value &&
    surfaceStore.user.id === props.post.author_id
      ? surfaceGuestStore.displayName
      : getAuthorDisplayName(author.value)
  return name
})

const authorProfileUrl = computed<string>(() => {
  return author.value?.profile_url || ''
})

const isRegistered = computed<boolean>(() => {
  // If a simulated attribution exists in the onboarding demo, we return false so that users can't go to the profile page
  if (props.post.hashid && surfaceSimulatedAttributionStore.attributions[props.post.hashid]) {
    return false
  }
  if (!author.value) return false
  return isUserRegistered(author.value)
})

const publishedAt = computed<string | Date>(() => {
  return getPostPublishedAt(props.post)
})

const isUsedInPostExpandedView = computed<boolean>(() => {
  return props.usageContext === 'postExpandedView'
})

const ringWithOffsetClass = computed<string>(() => {
  return postColorRingWithOffsetTailwindClass({
    postColor: props.post.color,
    isLightColorScheme: props.darkMode === false,
  })
})
</script>

<template>
  <div class="flex items-center text-12-16 font-sans">
    <div class="flex shrink-0">
      <component
        :is="isRegistered ? 'a' : 'span'"
        v-emit-click-on-touch
        :class="[
          'flex items-center self-start',
          isRegistered ? 'cursor-pointer group' : 'cursor-default',
          // Shape
          'rounded-full',
          // Focus
          'focus-visible:ring-2 ring-offset-2',
          ringWithOffsetClass,
        ]"
        :href="authorProfileUrl"
        target="_blank"
        data-bridged-link="profile"
        :data-testid="'surfacePostAuthor-' + username"
        :data-bridged-link-username="username"
        :aria-label="isRegistered ? __('Go to %{profileName} profile', { profileName: authorName }) : undefined"
        :title="isRegistered ? __('Go to %{profileName} profile', { profileName: authorName }) : undefined"
        @click="(event) => !device.app && event.stopPropagation()"
      >
        <avatar-image
          :user="isRegistered || surfaceGuestStore.shouldEnableAnonymousAttribution ? author : {}"
          :width="avatarSize"
          :height="avatarSize"
        />
      </component>
    </div>
    <div class="flex items-center min-w-0 text-12-12">
      <div
        :class="[
          'flex flex-col ms-2 space-y-0.5',
          {
            'text-light-text-100': darkMode === true,
            'text-dark-text-100': darkMode === false,
            'text-dark-text-100 dark:text-light-text-100': darkMode === 'auto',
          },
        ]"
      >
        <div :class="['flex space-x-0.5 hover-hover:group-hover:underline', !isTimelinePreview && 'me-[26px]']">
          <a
            v-if="isRegistered"
            :class="[
              'font-semibold',
              isTimelinePreview ? 'line-clamp-1' : 'block truncate',
              [
                'focus-visible:ring-2 focus-visible:rounded-sm',
                darkMode === true ? 'focus-visible:ring-canary-500' : 'focus-visible:ring-grape-500',
              ],
              {
                'text-light-text-100': darkMode === true,
                'text-dark-text-100': darkMode === false,
                'text-dark-text-100 dark:text-light-text-100': darkMode === 'auto',
              },
            ]"
            :href="authorProfileUrl"
            target="_blank"
            data-bridged-link="profile"
            :data-bridged-link-username="username"
            :aria-label="isRegistered ? __('Go to %{profileName} profile', { profileName: authorName }) : undefined"
            :title="isRegistered ? __('Go to %{profileName} profile', { profileName: authorName }) : undefined"
            @click="(event) => !device.app && event.stopPropagation()"
            >{{ authorName }}
          </a>
          <span v-else :class="['font-semibold', isTimelinePreview ? 'line-clamp-1' : 'block truncate']">
            {{ authorName }}
          </span>
          <div
            v-if="xAuthorRole && authorRole"
            :class="[
              {
                'text-light-text-200': darkMode === true,
                'text-dark-text-200': darkMode === false,
                'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto',
              },
              `flex before:content-['/'] after:content-['/']`,
            ]"
          >
            {{ authorRole }}
          </div>
          <SurfaceTranslationIndicator
            v-if="shouldShowTranslatedWishIcon && isUsedInPostExpandedView"
            :is-dark-mode="darkMode"
            :usage-context="SurfaceTranslationIndicatorUsageContext.Post"
            :is-translating="isWishBeingTranslated"
          />
        </div>

        <template v-if="publishedAt && xPublishedAt">
          <time
            v-if="isScreenshotMode"
            :class="[
              'font-normal',
              'text-12-14',
              {
                'text-light-text-200': darkMode === true,
                'text-dark-text-200': darkMode === false,
                'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto',
              },
              'shrink-0',
              'cursor-default',
            ]"
          >
            {{ formatDate(publishedAt, { year: 'numeric', month: 'short', day: 'numeric' }) }}
          </time>
          <OzRelativeTime
            v-else
            :class="[
              'font-normal',
              'text-12-14',
              {
                'text-light-text-200': darkMode === true,
                'text-dark-text-200': darkMode === false,
                'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto',
              },
              'shrink-0',
              'cursor-default',
            ]"
            :datetime="publishedAt"
            :dateformat="'long'"
            long-format="exact"
            data-autorefresh
          />
        </template>
      </div>
    </div>
    <SurfaceTranslationIndicator
      v-if="shouldShowTranslatedWishIcon && !isUsedInPostExpandedView"
      class="self-start ms-auto -me-0.75 mt-0.25"
      :is-dark-mode="darkMode"
      :usage-context="SurfaceTranslationIndicatorUsageContext.Post"
      :size="14"
      :is-translating="isWishBeingTranslated"
    />
  </div>
</template>
